import { Component, EventEmitter, Inject, OnInit } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { throwError } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { CONNECTION_ERROR_CODES } from '../../enums/connection-error-codes.enum';
import { DopModel } from '../../models/complex/dop.model';
import { MaterialIdentifierModel } from '../../models/low-level/material-identifier.model';
import { MATERIAL_STATUS_CODE } from '../../models/low-level/material-status.model';
import { MaterialModel } from '../../models/low-level/material.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { AppNotificationType } from '../../services/app-notification/app-notification.model';
import { AppNotificationService } from '../../services/app-notification/app-notification.service';
import { MaterialService } from '../../services/material/material.service';
import { assignModelOptions } from '../../utilities/assign-model-options';
import { MaterialPickupModel } from './material-pickup.model';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Component({
    selector: 'pace-core-material-pickup',
    templateUrl: './material-pickup.component.html',
    styleUrls: ['./material-pickup.component.scss'],
    standalone: false,
})
export class MaterialPickupComponent implements OnInit {
    public model: MaterialPickupModel;

    public id: NotificationIdentifierModel;
    public material: MaterialModel;
    public dopModel: DopModel;
    public displayNotReceived: boolean;
    public dopInfo: string[] = [];

    private availableMaterials: MaterialModel[];
    public displayBatchNumber: boolean;
    public displayPickupRFID: boolean;

    public materialPickupFormChildren: FormArray;
    public materialPickupForm: FormGroup;

    public modalResult = new EventEmitter<IMaterialPickupResult>();

    public isLoading = false;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly materialService: MaterialService,
        private readonly appNotificationService: AppNotificationService,
        public readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
    ) {
        this.displayBatchNumber = this.configService.getDisplayBatchNumber();
        this.displayPickupRFID = this.configService.getPickupRFIDEnabled();
    }

    public ngOnInit(): void {
        this.materialPickupFormChildren = this.formBuilder.array([]);
        this.materialPickupForm = this.formBuilder.group({
            formChildren: this.materialPickupFormChildren,
        });

        this.model = new MaterialPickupModel(this.id, this.material);
        if (this.dopModel) {
            this.dopInfo.push(this.dopModel.name1.value);
            this.dopInfo.push(this.dopModel.name2.value);
            this.dopInfo.push(this.dopModel.building.value);
            this.dopInfo.push(this.dopModel.floor.value);
            this.dopInfo.push(this.dopModel.room.value);
            this.dopInfo.push(this.dopModel.state.value);
            this.dopInfo.push(this.dopModel.department.value);
            const street = this.dopModel.street.value ? this.dopModel.street.value + ', ' : '';
            const info: string = <string>this.translate.instant('MATERIAL_PICKUP_CITY_STREET_POSTALCODE', {
                street,
                city: this.dopModel.city.value,
                postalCode: this.dopModel.postalCode.value,
            });
            this.dopInfo.push(info);
            this.dopInfo.push(this.dopModel.additionalInfo.value);
            this.dopInfo.push(this.dopModel.phone.value.toString());
        }
        this.availableMaterials = this.materialService.getAllOrderedMaterialsForNotification(this.id);
    }

    /**
     * In case of material "Not Received", MK code for that material will be set to 'X'.
     * and user will not be allowed to enter any other details for that material row.
     * However, "Consume" option will still be available for that material to revert the "Not received" action,
     * and it opens the "pick up missing" popup again to enable the pickup and then consumption of that material.
     */
    public setNotReceived(): void {
        // we only accept the "not received" if the user either entered only correct information or none at all.
        // If there is a mismatch, we need to tell the user to correct the data.
        if (this.model.selectedPartSet.awb.value.length === 0 || this.isMaterialCorrect()) {
            this.modalResult.next({
                materialModel: this.material,
                statusCode: MATERIAL_STATUS_CODE.NOTRECEIVED,
                pickedQuantity: this.model.selectedPartSet.quantity.value,
            });
            this.modalResult.complete();
            this.modalInstance.hide();
        }
    }

    public sendPickup(): void {
        if (this.isMaterialCorrect()) {
            this.isLoading = true;
            this.materialService
                .pickupMaterial(this.model)
                .pipe(
                    take(1),
                    tap(() => {
                        this.modalResult.next({
                            materialModel: this.material,
                            statusCode: MATERIAL_STATUS_CODE.PICKED,
                            pickedQuantity: this.model.selectedPartSet.quantity.value,
                        });
                        this.modalResult.complete();
                        this.modalInstance.hide();
                    }),
                    mergeMap(() => {
                        this.log.debug('sendPickup -> sendOfflineData');
                        return this.synchronizationService.sendOfflineData();
                    }),
                    catchError((error: CONNECTION_ERROR_CODES) => {
                        this.isLoading = false;
                        this.log.debug('sendPickup -> sendOfflineData: ERROR');
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.ERROR,
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                                : <string>this.translate.instant('MATERIAL_PICKUP_REQUEST_ERROR_TITLE'),
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                                : <string>this.translate.instant('MATERIAL_PICKUP_REQUEST_ERROR_BODY'),
                        );
                        return throwError(error);
                    }),
                )
                .subscribe();
        }
    }

    public checkValidQuantity(): boolean {
        let valid = false;
        const quantity = this.model.selectedPartSet.quantity.value;
        if (quantity && quantity > 0 && quantity <= this.model.deliveryQuantity) {
            valid = true;
        }

        return valid;
    }

    /**
     * For NGMAA, the material pickup is processed based on AWB number or Batch Number (depending on the config: DisplayBatchNumber).
     * If Batch Number is provided, then it must be validated against the material data present in NDPHeaderSet and let user proceed only if this is a correct match
     *
     * @returns {boolean}
     */
    private isMaterialCorrect(): boolean {
        if (this.displayBatchNumber) {
            const matchedMaterial = this.availableMaterials.find((material: MaterialModel) => {
                return (
                    material.batchNumber.value === this.model.selectedPartSet.awb.value &&
                    MaterialIdentifierModel.compare(
                        material.materialIdentifier,
                        new MaterialIdentifierModel(
                            this.model.selectedPartSet.materialNumber.value,
                            this.model.selectedPartSet.orderNumber.value,
                            this.model.selectedPartSet.orderPosition.value,
                        ),
                    )
                );
            });
            if (!matchedMaterial) {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.WARN,
                    <string>this.translate.instant('MATERIAL_PICKUP_VALIDATION_ERROR_INVALID_MATERIAL_TITLE'),
                    <string>this.translate.instant('MATERIAL_PICKUP_VALIDATION_ERROR_INVALID_MATERIAL_BODY'),
                );
                return false;
            }
        }
        return true;
    }

    public updateFormValidity(shouldEmitEvent = false): void {
        this.materialPickupFormChildren.controls.forEach((control) => {
            control.updateValueAndValidity({ emitEvent: shouldEmitEvent });
        });
    }

    public updateFeedbackComment(): void {
        if (this.model.selectedPartSet.feedbackComment && this.model.selectedPartSet.feedbackRating) {
            this.model.selectedPartSet.feedbackComment = assignModelOptions(
                this.model.selectedPartSet.feedbackComment,
                {
                    ...this.model.selectedPartSet.feedbackComment.options,
                    required:
                        parseInt(this.model.selectedPartSet.feedbackRating.value, 10) !==
                        this.model.selectedPartSet.feedbackRating.options.maxIcons,
                },
            );
            this.updateFormValidity();
        }
    }

    public onPickupRfidChange(): void {
        this.material.ucrNumber = this.model.selectedPartSet.pickupRfid.value;
    }
}

export interface IMaterialPickupResult {
    materialModel: MaterialModel;
    statusCode: MATERIAL_STATUS_CODE;
    pickedQuantity?: number;
}
